import { useState, useEffect } from 'react';
import { Gift } from 'lucide-react';
import { colors } from '../constants/colors';
import type { Listing } from '../types';
import type { ActiveGiveaway } from '../hooks/useWebSocket';
import { startGiveaway, drawGiveawayWinner, cancelGiveaway } from '../api/client';

interface GiveawayCardProps {
  showId: string | null;
  listings: Listing[];
  activeGiveaway: ActiveGiveaway | null;
  giveawayEnded: { giveawayId: string; winnerId?: string; winnerUsername?: string; entryCount: number; cancelled: boolean } | null;
  onGiveawayEnded?: () => void;
}

export function GiveawayCard({ showId, listings, activeGiveaway, giveawayEnded, onGiveawayEnded }: GiveawayCardProps) {
  const [selectedListingId, setSelectedListingId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Auto-clear giveaway ended display after 8 seconds
  useEffect(() => {
    if (giveawayEnded && !activeGiveaway) {
      const timer = setTimeout(() => {
        onGiveawayEnded?.();
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [giveawayEnded, activeGiveaway, onGiveawayEnded]);

  // Clear error when giveaway state changes
  useEffect(() => {
    setError(null);
  }, [activeGiveaway, giveawayEnded]);

  const availableListings = listings.filter(l => l.stock > 0);

  const handleStartGiveaway = async () => {
    if (!showId || !selectedListingId) return;
    setLoading(true);
    setError(null);
    try {
      await startGiveaway(showId, selectedListingId);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to start giveaway';
      setError(message);
      console.error('Failed to start giveaway:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDrawWinner = async () => {
    if (!activeGiveaway) return;
    setActionLoading('drawWinner');
    setError(null);
    try {
      await drawGiveawayWinner(activeGiveaway.id);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to draw winner';
      setError(message);
      console.error('Failed to draw winner:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelGiveaway = async () => {
    if (!activeGiveaway) return;
    setActionLoading('cancelGiveaway');
    setError(null);
    try {
      await cancelGiveaway(activeGiveaway.id);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to cancel giveaway';
      setError(message);
      console.error('Failed to cancel giveaway:', e);
    } finally {
      setActionLoading(null);
    }
  };

  // Active giveaway state
  if (activeGiveaway && activeGiveaway.status === 'ACTIVE') {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <Gift size={16} color="#8B5CF6" />
          <span style={styles.headerTitle}>Giveaway Active</span>
        </div>
        <div style={styles.liveInfo}>
          {activeGiveaway.item && (
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Item:</span>
              <span style={styles.infoValue}>{activeGiveaway.item.name}</span>
            </div>
          )}
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Entries:</span>
            <span style={styles.infoValue}>{activeGiveaway.entryCount}</span>
          </div>
        </div>
        {error && <div style={styles.errorMessage}>{error}</div>}
        <div style={styles.actions}>
          <button
            onClick={handleDrawWinner}
            disabled={actionLoading === 'drawWinner' || activeGiveaway.entryCount === 0}
            style={{
              ...styles.drawButton,
              ...(activeGiveaway.entryCount === 0 ? styles.drawButtonDisabled : {}),
            }}
          >
            {actionLoading === 'drawWinner' ? 'Drawing...' : 'Draw Winner'}
          </button>
          <button
            onClick={handleCancelGiveaway}
            disabled={actionLoading === 'cancelGiveaway'}
            style={styles.cancelButton}
          >
            {actionLoading === 'cancelGiveaway' ? '...' : 'Cancel'}
          </button>
        </div>
        {activeGiveaway.entryCount === 0 && (
          <p style={styles.hint}>Waiting for entries...</p>
        )}
      </div>
    );
  }

  // Giveaway ended state
  if (giveawayEnded && !activeGiveaway) {
    return (
      <div style={styles.card}>
        <div style={styles.endedContent}>
          <Gift size={16} color="#8B5CF6" />
          {giveawayEnded.cancelled ? (
            <span style={styles.endedText}>Giveaway cancelled</span>
          ) : giveawayEnded.winnerId ? (
            <span style={styles.endedText}>
              Winner: <strong>{giveawayEnded.winnerUsername || 'Unknown'}</strong> ({giveawayEnded.entryCount} entries)
            </span>
          ) : (
            <span style={styles.endedText}>Giveaway ended - No entries</span>
          )}
        </div>
        <button
          onClick={() => onGiveawayEnded?.()}
          style={styles.newGiveawayButton}
        >
          New Giveaway
        </button>
      </div>
    );
  }

  // Idle state - start a new giveaway
  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <Gift size={16} color="#8B5CF6" />
        <span style={styles.headerTitle}>Giveaway</span>
      </div>
      {availableListings.length === 0 ? (
        <p style={styles.hint}>No listings with stock available</p>
      ) : (
        <>
          <select
            value={selectedListingId}
            onChange={(e) => setSelectedListingId(e.target.value)}
            style={styles.select}
          >
            <option value="">Select an item...</option>
            {availableListings.map((listing) => (
              <option key={listing.id} value={listing.id}>
                {listing.name} ({listing.stock} in stock)
              </option>
            ))}
          </select>
          {error && <div style={styles.errorMessage}>{error}</div>}
          <button
            onClick={handleStartGiveaway}
            disabled={loading || !selectedListingId}
            style={{
              ...styles.startButton,
              ...(!selectedListingId ? styles.startButtonDisabled : {}),
            }}
          >
            {loading ? 'Starting...' : 'Start Giveaway'}
          </button>
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: colors.white,
    borderRadius: '12px',
    padding: '16px',
    border: `1px solid ${colors.gray[300]}`,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  headerTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#6D28D9',
  },
  liveInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '12px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoLabel: {
    fontSize: '13px',
    color: '#6D28D9',
  },
  infoValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: colors.ink.black,
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  drawButton: {
    flex: 1,
    padding: '10px 16px',
    backgroundColor: '#8B5CF6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  drawButtonDisabled: {
    backgroundColor: '#C4B5FD',
    cursor: 'not-allowed',
  },
  cancelButton: {
    padding: '10px 14px',
    backgroundColor: '#6B7280',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  hint: {
    margin: '8px 0 0 0',
    fontSize: '12px',
    color: '#6D28D9',
    textAlign: 'center' as const,
  },
  endedContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    justifyContent: 'center',
    marginBottom: '12px',
  },
  endedText: {
    fontSize: '14px',
    color: '#6D28D9',
  },
  newGiveawayButton: {
    width: '100%',
    padding: '10px 16px',
    backgroundColor: 'transparent',
    color: '#8B5CF6',
    border: '1px solid #8B5CF6',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    backgroundColor: colors.white,
    color: colors.ink.black,
    marginBottom: '12px',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  startButton: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: '#8B5CF6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  startButtonDisabled: {
    backgroundColor: '#C4B5FD',
    cursor: 'not-allowed',
  },
  errorMessage: {
    marginBottom: '12px',
    padding: '10px 12px',
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    border: '1px solid #FECACA',
  },
};
